import ArrowBack from "@mui/icons-material/ArrowBack";
import BookmarkAddOutlined from "@mui/icons-material/BookmarkAddOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import { Alert, Box, Button, IconButton, Link, Stack } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { UUID } from "../../utils/uuid";
import { useUpsertAssetCallback } from "../assets/hooks/use-upsert-asset";
import { DesignHuddleEditorWithBranding } from "../design-huddle/design-huddle-editor-with-branding";
import { useToken } from "../../server/auth/use-token";
import {
  DesignHuddleExportModal,
  type ExportHandler,
} from "../design-huddle/design-huddle-export-modal";
import { HeaderBar } from "../generic/components/header-bar";
import { LeavePageConfirm } from "../generic/components/leave-page-confirm";
import { UnsavedIndicator } from "../generic/components/unsaved-indicator";
import { AssetTagModal } from "../tags/asset-tag-modal";
import { EditableField } from "./editable-field";
import {
  linkDigitalAssetToContentGenerationResult,
  markContentGenerationResultExported,
} from "../content-generation/track-content-generation";
import { useSaveAssetCallback } from "./hooks/use-save-asset-callback";
import { SaveProgressIndicator } from "./save-progress-indicator";
import type { SaveAssetHandler } from "./types";

export const EditContent: FC<{
  projectId: string;
  page: number;
  onReady: () => void;
  title: string;
  onSave?: SaveAssetHandler;
  assetId?: UUID;
  multipage: boolean;
  contentType: string;
  contentGenerationResultId?: UUID;
}> = ({
  projectId,
  page,
  onReady,
  title,
  onSave,
  assetId,
  multipage,
  contentType,
  contentGenerationResultId,
}) => {
  const [filename, setFilename] = useState(title);
  const [fileNameError, setFileNameError] = useState<string | undefined>(undefined);
  const [saveProgress, setSaveProgress] = useState<number | null>(null);
  const exportModal = useDialog();
  const navigate = useNavigate();
  const dialog = useDialog();
  const upsertAsset = useUpsertAssetCallback();

  const [editable, setEditable] = useState(false);

  const getToken = useToken();

  const handleExport: ExportHandler = useCallback(
    ({ filename: f }) => {
      setFilename(f);
      if (contentGenerationResultId) {
        void getToken().then((token) =>
          markContentGenerationResultExported(token, contentGenerationResultId),
        );
      }
    },
    [contentGenerationResultId, getToken],
  );

  const saveAsset = useSaveAssetCallback({
    onSave,
    title: filename,
    projectId,
    multipage,
    contentType,
    id: assetId,
    onProgress: setSaveProgress,
  });

  const handleSetFilename = useCallback((newFileName: string) => {
    setFilename(newFileName);
    setFileNameError(undefined);
  }, []);

  const updateAssetTitle = useCallback(
    async (newTitle: string) => {
      if (!onSave) return;

      const { success, asset, error } = await onSave({ title: newTitle });

      if (!success || !asset) {
        if (error?.key === "name") {
          setFileNameError(error.message);
        }
      }
    },
    [onSave],
  );

  const [editorDirty, setEditorDirty] = useState(assetId ? false : true);
  const [saving, setSaving] = useState(false);
  const handleSave = useCallback(() => {
    let savingToast: string | undefined;
    void (async () => {
      try {
        setSaving(true);
        savingToast = toast.custom(<Alert severity="info">Saving asset...</Alert>, {
          duration: 3000,
        });
        const asset = await saveAsset();
        toast.dismiss(savingToast);
        if (asset?.success) {
          setEditorDirty(false);
          toast.custom(<SuccessToast />, { duration: 2000 });

          if (contentGenerationResultId) {
            void getToken().then((token) =>
              linkDigitalAssetToContentGenerationResult(
                token,
                contentGenerationResultId,
                asset.asset.id,
              ),
            );
          }

          DSHDLib.getProject(projectId, (projectErr, project) => {
            if (projectErr) {
              console.error("Error getting Design Huddle project:", projectErr);
              return;
            }

            void upsertAsset({ ...asset.asset, page_count: project.pages.length });
          });
        } else if (asset?.error?.key === "name") {
          setFileNameError(asset?.error.message);
          toast.error(asset?.error.message);
        } else throw new Error("Failed to save asset");
      } catch (e: unknown) {
        toast.error("There was an error saving your asset. Please try again later.");
        console.error(e);
      } finally {
        setSaving(false);
        setSaveProgress(null);
      }
    })();
  }, [projectId, saveAsset, upsertAsset, contentGenerationResultId, getToken]);

  return (
    <>
      <Box display="flex" flexDirection="column" height="100%">
        <HeaderBar navBurger>
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
            data-testid="edit-content-header"
            spacing={0}
          >
            <Button
              sx={{ px: 0, py: 0.5 }}
              data-analytics-id="edit-content-back"
              size="small"
              startIcon={<ArrowBack />}
              onClick={(e) => {
                e.preventDefault();
                if (window.history.length > 1) {
                  void navigate(-1);
                } else {
                  void navigate("/content-library");
                }
              }}
            >
              Back
            </Button>
            <Stack direction="row">
              <EditableField
                title={filename}
                editable={editable}
                setEditable={setEditable}
                setFilename={handleSetFilename}
                onChange={(newTitle: string) => void updateAssetTitle(newTitle)}
                error={fileNameError}
              />
              <IconButton
                data-analytics-id="edit-content-edit-name"
                size="small"
                data-testid="edit-content-edit-name-button"
                onClick={() => {
                  setEditable(true);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" gap={2} alignItems="center">
            {saving ? (
              <SaveProgressIndicator progress={saveProgress} />
            ) : (
              <UnsavedIndicator isDirty={editorDirty} />
            )}
            {assetId && (
              <Button
                data-analytics-id="edit-content-add-tags"
                variant="outlined"
                color="primary"
                size="large"
                startIcon={<LocalOfferOutlined />}
                onClick={dialog.handleOpen}
              >
                Add tags
              </Button>
            )}
            <Button
              data-analytics-id="edit-content-save-asset"
              variant="outlined"
              color={editorDirty ? "primary" : "secondary"}
              size="large"
              startIcon={editorDirty ? <BookmarkAddOutlined /> : <CheckCircleOutline />}
              onClick={editorDirty ? () => handleSave() : undefined}
              loading={saving}
            >
              {editorDirty ? "Save" : "Saved"}
            </Button>

            <Button
              data-analytics-id="edit-content-export-image"
              variant="contained"
              size="large"
              startIcon={<Download />}
              onClick={exportModal.handleOpen}
            >
              Export
            </Button>
          </Stack>
        </HeaderBar>
        <Box flexGrow={1}>
          {projectId !== "dummy" && (
            <DesignHuddleEditorWithBranding
              project_id={projectId}
              page_number={page}
              hide_right_panel={false}
              onReady={onReady}
              onChange={() => setEditorDirty(true)}
            />
          )}
        </Box>
        <DesignHuddleExportModal
          projectId={projectId}
          dialog={exportModal}
          filename={filename}
          setFilename={handleSetFilename}
          onExport={handleExport}
          saveHandler={handleSave}
          multipage={multipage}
        />
        <LeavePageConfirm
          enabled={() => editorDirty}
          slotProps={{
            title: { value: "Save your work before exiting" },
            proceedButton: { value: "Exit without saving" },
            stayButton: { value: "Save" },
          }}
          onStay={handleSave}
        >
          Save this design to your Content Library so you can view, edit, and duplicate it later.
        </LeavePageConfirm>
      </Box>
      {assetId && dialog.open && (
        <AssetTagModal open={dialog.open} assetId={assetId} onClose={dialog.handleClose} />
      )}
    </>
  );
};

const SuccessToast: FC = () => {
  return (
    <Alert severity="success">
      Saved to your <Link href="/content-library">Content Library</Link>
    </Alert>
  );
};
